import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getSchoolDashboardDataService } from "@/service/dashboard.service";
import { SchoolDashboardData } from "@/types/school-dashboard.model";

interface SchoolDashboardState {
  data: SchoolDashboardData | null;
  loading: boolean;
  error: string | null;
}

const initialState: SchoolDashboardState = {
  data: null,
  loading: false,
  error: null,
};

// Async thunk for fetching school dashboard data
export const fetchSchoolDashboardData = createAsyncThunk(
  "schoolDashboard/fetchData",
  async (schoolId?: string, { rejectWithValue }) => {
    try {
      const response = await getSchoolDashboardDataService(schoolId);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch school dashboard data"
      );
    }
  }
);

const schoolDashboardSlice = createSlice({
  name: "schoolDashboard",
  initialState,
  reducers: {
    clearSchoolDashboardData: (state) => {
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchoolDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSchoolDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchSchoolDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const { clearSchoolDashboardData } = schoolDashboardSlice.actions;

// Export selectors
export const selectSchoolDashboardData = (state: any) => 
  state.schoolDashboard.data;
export const selectSchoolDashboardLoading = (state: any) => 
  state.schoolDashboard.loading;
export const selectSchoolDashboardError = (state: any) => 
  state.schoolDashboard.error;

// Export reducer
export default schoolDashboardSlice.reducer;
